import { useEffect, useState } from 'react';
import { X, User } from 'lucide-react';
import { api } from '../lib/api';
import toast from 'react-hot-toast';

type ProfileResponse = {
  success: boolean;
  data?: {
    firstName?: string;
    lastName?: string;
    displayName?: string;
  };
};

interface PersonalDataRequiredModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: (data: { firstName: string; lastName: string; displayName: string }) => void;
  title?: string;
  description?: string;
}

export default function PersonalDataRequiredModal({
  isOpen,
  onClose,
  onSaved,
  title = 'Absenderdaten fehlen',
  description = 'Bitte hinterlegen Sie Ihren Namen (Vor-/Nachname oder Anzeigename), damit E-Mails einen Absender im Text enthalten.'
}: PersonalDataRequiredModalProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    (async () => {
      try {
        const res = await api.get<ProfileResponse>('/profile');
        const d = res.data?.data;
        setFirstName((d?.firstName || '').toString());
        setLastName((d?.lastName || '').toString());
        setDisplayName((d?.displayName || '').toString());
      } catch (e) {
        // silent; user can still enter manually
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen]);

  if (!isOpen) return null;

  const hasAnyName = () => {
    const full = `${firstName} ${lastName}`.trim();
    return Boolean(full) || Boolean(displayName.trim());
  };

  const handleSave = async () => {
    if (!hasAnyName()) {
      toast.error('Bitte geben Sie mindestens Vor-/Nachname oder einen Anzeigenamen an.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        displayName: displayName.trim(),
      };
      const res = await api.put('/profile', payload);
      if (res.data?.success) {
        // Best effort: keep cached user in sync for places that read from sessionStorage
        try {
          const raw = sessionStorage.getItem('user');
          if (raw) {
            const u = JSON.parse(raw);
            u.first_name = payload.firstName;
            u.last_name = payload.lastName;
            u.displayName = payload.displayName;
            sessionStorage.setItem('user', JSON.stringify(u));
          }
        } catch {}

        toast.success('Persönliche Angaben gespeichert');
        onSaved?.({ firstName: payload.firstName, lastName: payload.lastName, displayName: payload.displayName });
        onClose();
      } else {
        toast.error(res.data?.error || 'Speichern fehlgeschlagen');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-blue-100 rounded-lg">
              <User className="w-5 h-5 text-blue-700" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
              <p className="text-xs text-gray-600">{description}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 rounded hover:bg-gray-100 transition-colors"
            title="Schließen"
            disabled={saving}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {loading && (
            <div className="text-sm text-gray-500">Lade aktuelle Angaben…</div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Vorname</label>
              <input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Max"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Nachname</label>
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Mustermann"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Anzeigename (optional)</label>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="z.B. Frau Mustermann"
            />
            <p className="mt-1 text-xs text-gray-500">
              Tipp: Wenn Vor-/Nachname leer sind, wird der Anzeigename verwendet.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
            disabled={saving}
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
            disabled={saving}
          >
            {saving ? 'Speichere…' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}










